<script setup>
import { computed, ref, watch } from 'vue';
import { message } from 'ant-design-vue';
import { formatNumber } from '@/utils/formatNumber';
import formatDateTime from '@/utils/formatDateTime';
import { getOrderCheckoutInvoice } from '@/utils/orderInvoiceDisplay';
import {
    CopyOutlined,
    HistoryOutlined,
    ContainerOutlined,
    FileTextOutlined
} from '@ant-design/icons-vue';
import { getUserInventoryLogs } from '@/api/user-inventory';
import { getFormSubmissionLogs } from '@/api/forms';
import {
    buildInventoryLogDiff,
    buildSubmissionLogDiff,
    formatLogEvent
} from '@/utils/logFormatter';
import { canShowOrderStatusLog } from '@/utils/orderStatusHistory';
import {
    getPaymentMethodText as resolvePaymentMethodText,
    getPaymentMethodTagColor,
    getPaymentGatewayText as resolvePaymentGatewayText,
    getPaymentGatewayTagColor,
    getPaymentAccountText as resolvePaymentAccountText
} from '@/utils/paymentUtils';
import OrderStatusLogModal from '@/components/orders/OrderStatusLogModal.vue';

const props = defineProps({
    data: {
        type: Object,
        default: null
    },
    loading: {
        type: Boolean,
        default: false
    },
    canEditBank: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['edit-bank', 'edit-invoice']);

// --- 狀態 ---
const logsModalVisible = ref(false);
const logsLoading = ref(false);
const logsList = ref([]);
const logsTitle = ref('');
const logsType = ref(''); // 'inventory', 'submission'
const orderStatusLogVisible = ref(false);

// --- 常量與映射 ---
const ORDER_STATUS_MAP = {
    pending: { label: '待付款', color: 'orange' },
    paid: { label: '已付款', color: 'green' },
    completed: { label: '已完成', color: 'blue' },
    cancelled: { label: '已取消', color: 'default' },
    cancellation_pending: { label: '取消審核中', color: 'red' },
    refunding: { label: '退款中', color: 'orange' },
    partially_refunded: { label: '部分退款', color: 'orange' },
    refunded: { label: '已退款', color: 'default' },
    refund_failed: { label: '退款失敗', color: 'red' }
};

const ITEM_STATUS_MAP = {
    available: { label: '可使用', color: 'success' },
    used: { label: '使用中', color: 'processing' },
    course_completed: { label: '已完課', color: 'default' },
    expired: { label: '過期', color: 'default' },
    disabled: { label: '失效', color: 'error' }
};

const COMPLETION_STATUS_MAP = {
    not_submitted: { label: '未報名', color: 'default' },
    none: { label: '準備上課', color: 'processing' },
    completed: { label: '已完課', color: 'success' },
    absent: { label: '未完課', color: 'error' },
    cancelled: { label: '已取消', color: 'error' }
};

// --- 計算屬性 ---
const order = computed(() => props.data);

const checkoutInvoice = computed(() =>
    order.value ? getOrderCheckoutInvoice(order.value) : null
);

const invoiceGroupColumns = [
    { title: '分組編號', dataIndex: 'group_label', key: 'group_label', width: 132 },
    { title: '類型', dataIndex: 'group_type_label', key: 'group_type_label', width: 72 },
    { title: '商品', key: 'items', ellipsis: true },
    { title: '金額', key: 'amount', width: 96, align: 'right' },
    { title: '發票號碼', dataIndex: 'invoice_no', key: 'invoice_no', width: 120 },
    { title: '開立日期', dataIndex: 'invoice_date', key: 'invoice_date', width: 108 },
    { title: '場次日期', dataIndex: 'session_date', key: 'session_date', width: 108 },
    { title: '狀態', key: 'status', width: 88, align: 'center' }
];

const orderPayment = computed(() => {
    if (!order.value) return { method: null, gateway: null };
    const method =
        order.value.payment_method ?? order.value.payments?.[0]?.method ?? null;
    const gateway =
        order.value.payment_gateway ??
        order.value.payments?.[0]?.gateway ??
        null;
    return { method, gateway };
});

const cancellationRequest = computed(() => {
    return (
        order.value?.cancellation_requests?.[0] ||
        order.value?.cancellation_request ||
        null
    );
});

const detailActiveTab = ref('order');

const hasFulfillment = computed(
    () => (order.value?.item_form_status?.length ?? 0) > 0
);

const hasRefundSection = computed(() => {
    const o = order.value;
    if (!o) return false;
    if (cancellationRequest.value) return true;
    if (['refunded', 'partially_refunded', 'refunding'].includes(o.status)) {
        return true;
    }
    const amount = Number(o.actual_refund_amount || 0);
    if (amount > 0) return true;
    return (o.items || []).some((item) => Number(item.refunded_qty || 0) > 0);
});

watch(
    () => order.value?.id,
    () => {
        detailActiveTab.value = 'order';
    }
);

const itemColumns = [
    { title: '商品名稱', key: 'name', width: 200 },
    { title: '類型', key: 'type', width: 100 },
    { title: '單價', key: 'unit_price', width: 100, align: 'right' },
    { title: '數量', dataIndex: 'qty', width: 80, align: 'center' },
    { title: '小計', key: 'subtotal', width: 100, align: 'right' },
    { title: '寄送', key: 'shipping', width: 100 }
];

const statusColumns = [
    { title: '序', key: 'item_seq', width: 48, align: 'center' },
    { title: '項目名稱', key: 'name' },
    { title: '物品狀態', key: 'item_status', width: 100 },
    { title: '報名/完課狀態', key: 'comp_status', width: 120 },
    { title: '報名場次', key: 'session', width: 150 },
    { title: '操作', key: 'actions', width: 160, align: 'center' }
];

// --- 輔助函式 ---
const formatAmount = (val) => {
    if (!val && val !== 0) return '-';
    return Number(val).toLocaleString('zh-TW');
};

const getShippingMethodText = (method) => {
    const methodMap = {
        home_delivery: '宅配',
        convenience_store: '超商取貨',
        post_office: '郵局'
    };
    return methodMap[method] || method || '-';
};

const getPaymentMethodText = (method) => {
    return resolvePaymentMethodText(method);
};

const getPaymentGatewayText = (gateway) => {
    return resolvePaymentGatewayText(gateway);
};

const getPaymentAccountText = (order) => {
    return resolvePaymentAccountText(order);
};

const getRefundStatusText = (status) => {
    const map = {
        pending: '待處理',
        processing: '退款中',
        completed: '已退款',
        failed: '退款失敗'
    };
    return map[status] || status || '-';
};

const getRefundStatusColor = (status) => {
    const map = {
        pending: 'orange',
        processing: 'blue',
        completed: 'green',
        failed: 'red'
    };
    return map[status] || 'default';
};

const PAYMENT_STATUS_MAP = {
    initiated: '已初始化',
    captured: '已完成',
    failed: '失敗',
    cancelled: '已取消',
    expired: '已過期',
    refunded: '已退款',
    pending: '待處理',
    processing: '處理中',
    completed: '已完成'
};

const getPaymentStatusText = (status) => {
    const key = status?.toLowerCase();
    return PAYMENT_STATUS_MAP[key] || status || '-';
};

const getPaymentStatusColor = (status) => {
    const key = status?.toLowerCase();
    const colorMap = {
        initiated: 'blue',
        captured: 'green',
        failed: 'red',
        cancelled: 'default',
        expired: 'orange',
        refunded: 'purple',
        pending: 'orange',
        processing: 'blue',
        completed: 'green'
    };
    return colorMap[key] || 'blue';
};

const TRANSACTION_STATUS_MAP = {
    success: '成功',
    fail: '失敗',
    failed: '失敗',
    error: '錯誤',
    manual_creation: '手動建立',
    initiated: '已建立',
    captured: '已完成',
    pending: '處理中',
    cancelled: '已取消',
    refunded: '已退款'
};

const getTransactionStatusText = (status) => {
    const key = status?.toLowerCase();
    return TRANSACTION_STATUS_MAP[key] || status || '-';
};

const getTransactionStatusColor = (status) => {
    const key = status?.toLowerCase();
    if (['success', 'captured', 'completed'].includes(key)) return 'green';
    if (['manual_creation', 'initiated', 'pending'].includes(key))
        return 'blue';
    if (['fail', 'failed', 'error'].includes(key)) return 'red';
    return 'default';
};

const formatJsonString = (json) => {
    if (!json) return '';
    try {
        const obj = typeof json === 'string' ? JSON.parse(json) : json;
        return JSON.stringify(obj, null, 2);
    } catch {
        return String(json);
    }
};

const copyToClipboard = async (text) => {
    if (!text) return;
    try {
        await navigator.clipboard.writeText(
            typeof text === 'string' ? text : JSON.stringify(text)
        );
        message.success('已複製到剪貼簿');
    } catch {
        message.error('複製失敗');
    }
};

// --- 操作紀錄方法 ---
const openInventoryLogs = async (it) => {
    logsType.value = 'inventory';
    logsTitle.value = `物品操作紀錄 - ${it.item_name}`;
    logsModalVisible.value = true;
    logsLoading.value = true;
    logsList.value = [];
    try {
        const res = await getUserInventoryLogs(it.user_inventory_id);
        logsList.value = res.data || [];
    } catch {
        message.error('載入物品紀錄失敗');
    } finally {
        logsLoading.value = false;
    }
};

const openSubmissionLogs = async (it) => {
    logsType.value = 'submission';
    logsTitle.value = `表單提交紀錄 - ${it.item_name}`;
    logsModalVisible.value = true;
    logsLoading.value = true;
    logsList.value = [];
    try {
        const res = await getFormSubmissionLogs(it.form_submission_id);
        logsList.value = res.data || [];
    } catch {
        message.error('載入表單紀錄失敗');
    } finally {
        logsLoading.value = false;
    }
};

const openOrderLogs = () => {
    if (!order.value?.id || !canShowOrderStatusLog(order.value)) return;
    orderStatusLogVisible.value = true;
};

const getActualRefundAmount = (record) => {
    if (!record) return 0;
    const amount = record.actual_refund_amount || record.cancellation_request?.actual_refund_amount || record.cancellation_requests?.[0]?.actual_refund_amount;
    if (amount !== undefined && amount !== null && Number(amount) > 0) {
        return Number(amount);
    }
    if (record.items && record.items.length > 0) {
        const itemSum = record.items.reduce((sum, item) => sum + Number(item.refunded_amount || 0), 0);
        if (itemSum > 0) return itemSum;
    }
    if (record.status === 'refunded') {
        return Number(record.grand_total || record.subtotal || 0);
    }
    return 0;
};

const getRefundItems = (orderObj) => {
    if (!orderObj || !orderObj.items || !Array.isArray(orderObj.items)) return [];
    const refundedItems = orderObj.items.filter(item => Number(item.refunded_qty || 0) > 0);
    if (refundedItems.length > 0) {
        return refundedItems.map(item => ({
            name: item.name_snapshot || item.name || item.product?.name || '商品',
            qty: item.refunded_qty || item.qty || 1
        }));
    }
    return orderObj.items.map(item => ({
        name: item.name_snapshot || item.name || item.product?.name || '商品',
        qty: item.qty || 1
    }));
};
</script>

<template>
    <div class="order-detail-content-wrapper">
        <a-spin :spinning="loading">
            <template v-if="order">
                <a-tabs v-model:activeKey="detailActiveTab" class="vaporwave-tabs">
                    <a-tab-pane key="order" tab="訂單">
                        <div class="tab-scroll-content">
<!-- 1. 基本資訊 -->
                            <div class="detail-section mb-6">
                                <div
                                    class="flex justify-between items-center mb-4"
                                >
                                    <h3 class="section-title mb-0">基本資訊</h3>
                                    <a-button
                                        v-if="canShowOrderStatusLog(order)"
                                        type="default"
                                        size="small"
                                        class="btn-secondary"
                                        @click="openOrderLogs"
                                    >
                                        <template #icon>
                                            <HistoryOutlined />
                                        </template>
                                        狀態歷程
                                    </a-button>
                                </div>
                                <a-descriptions
                                    bordered
                                    size="small"
                                    :column="2"
                                >
                                    <a-descriptions-item label="訂單編號">
                                        {{ order.order_no }}
                                    </a-descriptions-item>
                                    <a-descriptions-item label="下單時間">
                                        {{ formatDateTime(order.placed_at) }}
                                    </a-descriptions-item>
                                    <a-descriptions-item label="會員姓名">
                                        {{ order.user?.name || '-' }}
                                    </a-descriptions-item>
                                    <a-descriptions-item label="會員電話">
                                        {{ order.user?.phone || '-' }}
                                    </a-descriptions-item>
                                    <a-descriptions-item label="訂單狀態">
                                        <a-tag
                                            :color="
                                                ORDER_STATUS_MAP[order.status]
                                                    ?.color
                                            "
                                        >
                                            {{
                                                order.status_label ||
                                                ORDER_STATUS_MAP[order.status]
                                                    ?.label ||
                                                order.status
                                            }}
                                        </a-tag>
                                    </a-descriptions-item>
                                    <a-descriptions-item label="完款日期">
                                        {{ order.paid_at || '-' }}
                                    </a-descriptions-item>
                                </a-descriptions>
                            </div>

                            <!-- 2. 訂單商品 -->
                            <div class="detail-section mb-6">
                                <h3 class="section-title">訂單商品</h3>
                                <a-table
                                    :dataSource="order.items || []"
                                    :columns="itemColumns"
                                    :pagination="false"
                                    size="small"
                                    bordered
                                    rowKey="id"
                                    :scroll="{ x: 680 }"
                                >
                                    <template
                                        #bodyCell="{ column, record: it }"
                                    >
                                        <template v-if="column.key === 'name'">
                                            {{ it.name_snapshot }}
                                        </template>
                                        <template
                                            v-else-if="column.key === 'type'"
                                        >
                                            <a-tag color="cyan">
                                                {{
                                                    it.product?.type_display ||
                                                    it.type_snapshot_display ||
                                                    it.type_snapshot
                                                }}
                                            </a-tag>
                                        </template>
                                        <template
                                            v-else-if="
                                                column.key === 'unit_price'
                                            "
                                        >
                                            ${{
                                                formatAmount(
                                                    it.unit_price_snapshot
                                                )
                                            }}
                                        </template>
                                        <template
                                            v-else-if="
                                                column.key === 'subtotal'
                                            "
                                        >
                                            ${{ formatAmount(it.subtotal) }}
                                        </template>
                                        <template
                                            v-else-if="
                                                column.key === 'shipping'
                                            "
                                        >
                                            <a-tag
                                                :color="
                                                    it.requires_shipping
                                                        ? 'orange'
                                                        : 'default'
                                                "
                                            >
                                                {{
                                                    it.requires_shipping
                                                        ? '需寄送'
                                                        : '無需寄送'
                                                }}
                                            </a-tag>
                                        </template>
                                    </template>
                                </a-table>
                            </div>

                            <!-- 3. 收件資訊 -->
                            <div
                                class="detail-section mb-6"
                                v-if="
                                    order.recipient_name ||
                                    order.recipient_phone ||
                                    order.recipient_address
                                "
                            >
                                <h3 class="section-title">收件資訊</h3>
                                <a-descriptions
                                    bordered
                                    size="small"
                                    :column="2"
                                >
                                    <a-descriptions-item label="收件人">
                                        {{ order.recipient_name }}
                                    </a-descriptions-item>
                                    <a-descriptions-item label="聯絡電話">
                                        {{ order.recipient_phone }}
                                    </a-descriptions-item>
                                    <a-descriptions-item
                                        label="收件地址"
                                        :span="2"
                                    >
                                        {{ order.recipient_address || '-' }}
                                    </a-descriptions-item>
                                    <a-descriptions-item label="寄送方式">
                                        {{
                                            getShippingMethodText(
                                                order.shipping_method
                                            )
                                        }}
                                    </a-descriptions-item>
                                </a-descriptions>
                            </div>
                        </div>
                    </a-tab-pane>

                    <a-tab-pane key="invoice" tab="發票">
                        <div class="tab-scroll-content">
<!-- 4. 發票資訊 -->
                            <div class="detail-section mb-6">
                                <div
                                    class="flex justify-between items-center mb-4"
                                >
                                    <h3 class="section-title mb-0">發票需求</h3>
                                    <a-button
                                        v-if="canEditBank"
                                        type="default"
                                        size="small"
                                        class="btn-secondary"
                                        @click="emit('edit-invoice')"
                                    >
                                        編輯發票資料
                                    </a-button>
                                </div>
                                <a-descriptions
                                    bordered
                                    size="small"
                                    :column="2"
                                >
                                    <a-descriptions-item label="發票類型">
                                        {{
                                            checkoutInvoice?.typeLabel || '-'
                                        }}
                                        <template
                                            v-if="
                                                checkoutInvoice?.type ===
                                                'personal'
                                            "
                                        >
                                            （個人）
                                        </template>
                                        <template
                                            v-else-if="
                                                checkoutInvoice?.type ===
                                                'company'
                                            "
                                        >
                                            （公司）
                                        </template>
                                    </a-descriptions-item>
                                    <a-descriptions-item label="統一編號">
                                        {{
                                            checkoutInvoice?.vatNumber || '-'
                                        }}
                                    </a-descriptions-item>
                                    <a-descriptions-item label="載具/捐贈碼">
                                        <span
                                            v-if="
                                                checkoutInvoice?.type ===
                                                'carrier'
                                            "
                                            class="invoice-mono"
                                        >
                                            {{ checkoutInvoice.carrierCode }}
                                        </span>
                                        <span
                                            v-else-if="
                                                checkoutInvoice?.type ===
                                                'donation'
                                            "
                                        >
                                            捐贈
                                            {{ checkoutInvoice.carrierCode }}
                                        </span>
                                        <span v-else>-</span>
                                    </a-descriptions-item>
                                    <a-descriptions-item label="發票抬頭">
                                        {{ checkoutInvoice?.title || '-' }}
                                    </a-descriptions-item>
                                </a-descriptions>
                            </div>

                            <!-- 4.1 發票分組（付款後依商品類型拆分） -->
                            <div
                                v-if="order.invoice_groups?.length"
                                class="detail-section mb-6"
                            >
                                <div class="mb-4">
                                    <h3 class="section-title mb-0">開立發票</h3>
                                </div>
                                <a-table
                                    size="small"
                                    bordered
                                    :pagination="false"
                                    row-key="id"
                                    :columns="invoiceGroupColumns"
                                    :data-source="order.invoice_groups"
                                >
                                    <template #bodyCell="{ column, record }">
                                        <template
                                            v-if="column.key === 'items'"
                                        >
                                            <div
                                                v-for="item in record.items"
                                                :key="item.id"
                                                class="text-xs"
                                            >
                                                {{ item.name_snapshot }}
                                            </div>
                                        </template>
                                        <template
                                            v-else-if="
                                                column.key === 'status'
                                            "
                                        >
                                            <a-tag
                                                :color="
                                                    record.status === 'invoiced'
                                                        ? 'success'
                                                        : record.status ===
                                                            'void'
                                                          ? 'default'
                                                          : 'warning'
                                                "
                                            >
                                                {{
                                                    record.status_label ||
                                                    record.status
                                                }}
                                            </a-tag>
                                        </template>
                                        <template
                                            v-else-if="
                                                column.key === 'amount'
                                            "
                                        >
                                            ${{
                                                formatNumber(record.amount || 0)
                                            }}
                                        </template>
                                    </template>
                                </a-table>
                            </div>

                                                    </div>
                    </a-tab-pane>

                    <a-tab-pane key="payment" tab="付款">
                        <div class="tab-scroll-content">
<!-- 5. 付款資訊 -->
                            <div class="detail-section mb-6">
                                <div
                                    class="flex justify-between items-center mb-4"
                                >
                                    <h3 class="section-title mb-0">付款資訊</h3>
                                    <a-button
                                        v-if="
                                            orderPayment.gateway ===
                                                'bank_transfer' && canEditBank
                                        "
                                        type="default"
                                        size="small"
                                        class="btn-secondary"
                                        @click="emit('edit-bank')"
                                    >
                                        編輯匯款資料
                                    </a-button>
                                </div>
                                <a-descriptions
                                    bordered
                                    size="small"
                                    :column="2"
                                >
                                    <a-descriptions-item label="付款方式">
                                        <a-tag
                                            :color="
                                                getPaymentMethodTagColor(
                                                    orderPayment.method
                                                )
                                            "
                                        >
                                            {{
                                                getPaymentMethodText(
                                                    orderPayment.method
                                                )
                                            }}
                                        </a-tag>
                                    </a-descriptions-item>
                                    <a-descriptions-item label="付款閘道">
                                        <a-tag
                                            :color="
                                                getPaymentGatewayTagColor(
                                                    orderPayment.gateway
                                                )
                                            "
                                        >
                                            {{
                                                getPaymentGatewayText(
                                                    orderPayment.gateway
                                                )
                                            }}
                                        </a-tag>
                                    </a-descriptions-item>
                                    <a-descriptions-item label="收款帳號">
                                        {{ getPaymentAccountText(order) }}
                                    </a-descriptions-item>

                                    <template
                                        v-if="
                                            orderPayment.gateway ===
                                                'bank_transfer' ||
                                            order.payment_info ||
                                            order.account_last_5
                                        "
                                    >
                                        <a-descriptions-item label="匯款日期">
                                            {{
                                                order.transfer_date ||
                                                order.payment_info
                                                    ?.transfer_date ||
                                                '-'
                                            }}
                                        </a-descriptions-item>
                                        <a-descriptions-item label="匯款銀行">
                                            {{
                                                order.transfer_bank ||
                                                order.payment_info
                                                    ?.transfer_bank ||
                                                '-'
                                            }}
                                        </a-descriptions-item>
                                        <a-descriptions-item label="匯款戶名">
                                            {{
                                                order.account_name ||
                                                order.payment_info
                                                    ?.account_name ||
                                                '-'
                                            }}
                                        </a-descriptions-item>
                                        <a-descriptions-item label="帳號後五碼">
                                            <span
                                                class="text-purple-600 font-bold"
                                            >
                                                {{
                                                    order.account_last_5 ||
                                                    order.payment_info
                                                        ?.account_last_5 ||
                                                    '-'
                                                }}
                                            </span>
                                        </a-descriptions-item>
                                    </template>
                                    <a-descriptions-item
                                        v-else
                                        label="交易編號"
                                        :span="2"
                                    >
                                        {{
                                            order.payment_no ||
                                            order.payment_info?.payment_no ||
                                            '-'
                                        }}
                                    </a-descriptions-item>

                                    <a-descriptions-item label="積分抵扣">
                                        <div
                                            v-if="
                                                order.point_discount_total > 0
                                            "
                                        >
                                            <span
                                                class="text-red-500 font-bold"
                                            >
                                                -{{
                                                    formatAmount(
                                                        order.point_discount_total
                                                    )
                                                }}
                                            </span>
                                            <div
                                                v-if="
                                                    order.point_discounts
                                                        ?.length
                                                "
                                                class="mt-1"
                                            >
                                                <a-tag
                                                    v-for="pd in order.point_discounts"
                                                    :key="pd.id"
                                                    class="text-[10px]"
                                                >
                                                    {{
                                                        pd.point_type?.code ||
                                                        pd.point_type?.name ||
                                                        '點數'
                                                    }}: -{{
                                                        formatAmount(pd.amount)
                                                    }}
                                                </a-tag>
                                            </div>
                                        </div>
                                        <span v-else>無</span>
                                    </a-descriptions-item>
                                    <a-descriptions-item
                                        label="應付總額"
                                        :span="2"
                                    >
                                        <span
                                            class="total-amount font-mono font-bold text-xl text-purple-600"
                                        >
                                            ${{
                                                formatAmount(order.grand_total)
                                            }}
                                        </span>
                                    </a-descriptions-item>
                                </a-descriptions>
                            </div>

                            <!-- 6. 取消申請 / 退款資訊 -->
                            <div
                                class="detail-section mb-6"
                                v-if="hasRefundSection"
                            >
                                <h3 class="section-title">{{ cancellationRequest ? '取消申請資訊' : '退款明細資訊' }}</h3>
                                <a-descriptions
                                    bordered
                                    size="small"
                                    :column="2"
                                >
                                    <template v-if="cancellationRequest">
                                        <a-descriptions-item label="申請狀態">
                                            <a-tag
                                                :color="
                                                    cancellationRequest.status ===
                                                    'approved'
                                                        ? 'green'
                                                        : cancellationRequest.status ===
                                                            'rejected'
                                                          ? 'red'
                                                          : 'orange'
                                                "
                                            >
                                                {{
                                                    cancellationRequest.status_label ||
                                                    (cancellationRequest.status ===
                                                    'approved'
                                                        ? '已核准'
                                                        : cancellationRequest.status ===
                                                            'rejected'
                                                          ? '已拒絕'
                                                          : '待審核')
                                                }}
                                            </a-tag>
                                        </a-descriptions-item>
                                        <a-descriptions-item label="退款狀態">
                                            <a-tag
                                                :color="
                                                    getRefundStatusColor(
                                                        cancellationRequest.refund_status
                                                    )
                                                "
                                            >
                                                {{
                                                    getRefundStatusText(
                                                        cancellationRequest.refund_status
                                                    )
                                                }}
                                            </a-tag>
                                        </a-descriptions-item>
                                        <a-descriptions-item label="申請時間">
                                            {{
                                                formatDateTime(
                                                    cancellationRequest.created_at
                                                )
                                            }}
                                        </a-descriptions-item>
                                        <a-descriptions-item
                                            label="取消原因"
                                            :span="2"
                                        >
                                            {{ cancellationRequest.reason || '-' }}
                                        </a-descriptions-item>
                                        <a-descriptions-item
                                            label="備註"
                                            :span="2"
                                            v-if="cancellationRequest.note"
                                        >
                                            {{ cancellationRequest.note }}
                                        </a-descriptions-item>
                                    </template>
                                    <template v-else>
                                        <a-descriptions-item label="退款狀態">
                                            <a-tag :color="ORDER_STATUS_MAP[order.status]?.color || 'orange'">
                                                {{ ORDER_STATUS_MAP[order.status]?.label || order.status }}
                                            </a-tag>
                                        </a-descriptions-item>
                                        <a-descriptions-item label="實際退款金額">
                                            <span class="font-bold text-cyan-600 font-mono">
                                                ${{ formatAmount(getActualRefundAmount(order)) }}
                                            </span>
                                        </a-descriptions-item>
                                        <a-descriptions-item label="行政手續費" v-if="order.fee_amount > 0">
                                            <span class="text-red-500 font-mono font-bold">
                                                ${{ formatAmount(order.fee_amount) }}
                                            </span>
                                            <span v-if="order.fee_note" class="text-gray-400 text-xs ml-1">
                                                ({{ order.fee_note }})
                                            </span>
                                        </a-descriptions-item>
                                        <a-descriptions-item label="退款說明" :span="order.fee_amount > 0 ? 1 : 2">
                                            {{ order.refund_note || order.fee_note || '-' }}
                                        </a-descriptions-item>
                                        <a-descriptions-item label="退款品項" :span="2">
                                            <div v-if="getRefundItems(order).length > 0" class="flex flex-wrap gap-2">
                                                <a-tag v-for="(it, idx) in getRefundItems(order)" :key="idx" color="blue">
                                                    {{ it.name }} x {{ it.qty }}
                                                </a-tag>
                                            </div>
                                            <span v-else>-</span>
                                        </a-descriptions-item>
                                    </template>
                                </a-descriptions>
                            </div>

                            
                            <div class="detail-section mb-6">
                                <h3 class="section-title">金流紀錄</h3>
                                <div
                                    v-if="
                                        order.payments &&
                                        order.payments.length > 0
                                    "
                                >
                                <div
                                    v-for="payment in order.payments"
                                    :key="payment.id"
                                    class="payment-card mb-8 border rounded-lg overflow-hidden"
                                >
                                    <div
                                        class="bg-gray-100 p-2 font-bold flex justify-between items-center"
                                    >
                                        <span>支付記錄 {{ payment.id }}</span>
                                        <a-tag
                                            :color="
                                                getPaymentStatusColor(
                                                    payment.status
                                                )
                                            "
                                        >
                                            {{
                                                getPaymentStatusText(
                                                    payment.status
                                                )
                                            }}
                                        </a-tag>
                                    </div>
                                    <a-descriptions
                                        :column="2"
                                        size="small"
                                        bordered
                                    >
                                        <a-descriptions-item label="支付編號">
                                            {{ payment.payment_no }}
                                        </a-descriptions-item>
                                        <a-descriptions-item label="支付金額">
                                            ${{ formatAmount(payment.amount) }}
                                        </a-descriptions-item>
                                        <a-descriptions-item label="支付方式">
                                            {{
                                                getPaymentMethodText(
                                                    payment.method
                                                )
                                            }}
                                            ({{
                                                getPaymentGatewayText(
                                                    payment.gateway
                                                )
                                            }})
                                        </a-descriptions-item>
                                        <a-descriptions-item label="支付時間">
                                            {{
                                                formatDateTime(
                                                    payment.paid_at
                                                ) || '-'
                                            }}
                                        </a-descriptions-item>
                                    </a-descriptions>

                                    <!-- 交易記錄 -->
                                    <div
                                        class="p-4"
                                        v-if="
                                            payment.transactions &&
                                            payment.transactions.length > 0
                                        "
                                    >
                                        <h4
                                            class="font-bold mb-2 border-l-4 border-purple-400 pl-2"
                                        >
                                            交易日誌 (Log)
                                        </h4>
                                        <div
                                            v-for="trx in payment.transactions"
                                            :key="trx.id"
                                            class="mb-4 border-b pb-4 last:border-0"
                                        >
                                            <div
                                                class="flex justify-between items-center mb-2"
                                            >
                                                <span class="text-xs font-mono">
                                                    ID:
                                                    {{
                                                        trx.gateway_trade_no ||
                                                        trx.id
                                                    }}
                                                </span>
                                                <a-space>
                                                    <span
                                                        class="text-xs text-gray-400"
                                                    >
                                                        {{
                                                            formatDateTime(
                                                                trx.occurred_at
                                                            )
                                                        }}
                                                    </span>
                                                    <a-tag
                                                        :color="
                                                            getTransactionStatusColor(
                                                                trx.status
                                                            )
                                                        "
                                                    >
                                                        {{
                                                            getTransactionStatusText(
                                                                trx.status
                                                            )
                                                        }}
                                                    </a-tag>
                                                </a-space>
                                            </div>
                                            <a-collapse
                                                :bordered="false"
                                                size="small"
                                            >
                                                <a-collapse-panel
                                                    key="req"
                                                    header="請求資料 (Request)"
                                                >
                                                    <template #extra>
                                                        <a-button
                                                            type="text"
                                                            size="small"
                                                            @click.stop="
                                                                copyToClipboard(
                                                                    trx.req_payload
                                                                )
                                                            "
                                                        >
                                                            <CopyOutlined />
                                                        </a-button>
                                                    </template>
                                                    <pre
                                                        class="text-[10px] bg-gray-900 text-green-400 p-2 rounded overflow-x-auto"
                                                        >{{
                                                            formatJsonString(
                                                                trx.req_payload
                                                            )
                                                        }}</pre
                                                    >
                                                </a-collapse-panel>
                                                <a-collapse-panel
                                                    key="resp"
                                                    header="回應資料 (Response)"
                                                >
                                                    <template #extra>
                                                        <a-button
                                                            type="text"
                                                            size="small"
                                                            @click.stop="
                                                                copyToClipboard(
                                                                    trx.resp_payload
                                                                )
                                                            "
                                                        >
                                                            <CopyOutlined />
                                                        </a-button>
                                                    </template>
                                                    <pre
                                                        class="text-[10px] bg-gray-900 text-blue-400 p-2 rounded overflow-x-auto"
                                                        >{{
                                                            formatJsonString(
                                                                trx.resp_payload
                                                            )
                                                        }}</pre
                                                    >
                                                </a-collapse-panel>
                                            </a-collapse>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <a-empty v-else description="尚無詳細金流記錄" />
                            </div>
                        </div>
                    </a-tab-pane>

                    <a-tab-pane key="fulfillment" tab="履約">
                        <div class="tab-scroll-content">
                            <div
                                v-if="hasFulfillment"
                                class="detail-section detail-section--status"
                            >
                                <h3 class="section-title">履約狀態</h3>
                                <a-table
                                    class="status-table"
                                    :dataSource="order.item_form_status"
                                    :columns="statusColumns"
                                    :pagination="false"
                                    size="small"
                                    bordered
                                    rowKey="item_id"
                                >
                                    <template
                                        #bodyCell="{ column, record: it }"
                                    >
                                        <template
                                            v-if="column.key === 'item_seq'"
                                        >
                                            <span class="item-seq">
                                                {{ it.item_seq }}
                                            </span>
                                        </template>
                                        <template
                                            v-else-if="column.key === 'name'"
                                        >
                                            {{ it.item_name }}
                                        </template>
                                        <template
                                            v-else-if="
                                                column.key === 'item_status'
                                            "
                                        >
                                            <a-tag
                                                :color="
                                                    ITEM_STATUS_MAP[
                                                        it.item_status
                                                    ]?.color
                                                "
                                            >
                                                {{ it.item_status_label }}
                                            </a-tag>
                                        </template>
                                        <template
                                            v-else-if="
                                                column.key === 'comp_status'
                                            "
                                        >
                                            <a-tag
                                                :color="
                                                    COMPLETION_STATUS_MAP[
                                                        it.completion_status
                                                    ]?.color
                                                "
                                            >
                                                {{ it.completion_status_label }}
                                            </a-tag>
                                        </template>
                                        <template
                                            v-else-if="column.key === 'session'"
                                        >
                                            {{ it.form_session?.name || '-' }}
                                        </template>
                                        <template
                                            v-else-if="column.key === 'actions'"
                                        >
                                            <div class="status-actions">
                                                <a
                                                    v-if="it.user_inventory_id"
                                                    class="action-link"
                                                    href="javascript:void(0)"
                                                    @click.prevent="
                                                        openInventoryLogs(it)
                                                    "
                                                >
                                                    <ContainerOutlined />
                                                    物品紀錄
                                                </a>
                                                <a
                                                    v-if="it.form_submission_id"
                                                    class="action-link"
                                                    href="javascript:void(0)"
                                                    @click.prevent="
                                                        openSubmissionLogs(it)
                                                    "
                                                >
                                                    <FileTextOutlined />
                                                    表單紀錄
                                                </a>
                                                <span
                                                    v-if="
                                                        !it.user_inventory_id &&
                                                        !it.form_submission_id
                                                    "
                                                    class="action-empty"
                                                >
                                                    —
                                                </span>
                                            </div>
                                        </template>
                                    </template>
                                </a-table>
                            </div>
                            <a-empty v-else description="尚無履約資料" />
                        </div>
                    </a-tab-pane>
                </a-tabs>
            </template>
        </a-spin>

        <!-- 訂單狀態歷程 -->
        <OrderStatusLogModal
            v-model:open="orderStatusLogVisible"
            :order-id="order?.id"
            :order-no="order?.order_no"
        />

        <!-- 物品／表單操作紀錄彈窗 -->
        <a-modal
            v-model:open="logsModalVisible"
            :title="logsTitle"
            :width="480"
            :footer="null"
            :z-index="1100"
            destroy-on-close
            wrap-class-name="order-logs-modal"
            @cancel="logsModalVisible = false"
        >
            <a-spin :spinning="logsLoading">
                <div class="logs-modal-body">
                    <a-empty
                        v-if="!logsLoading && !logsList.length"
                        description="尚無操作紀錄"
                        class="logs-empty"
                    />
                    <div v-else-if="logsList.length" class="logs-list">
                        <div
                            v-for="log in logsList"
                            :key="log.id"
                            class="log-row"
                        >
                            <div class="log-row__head">
                                <span class="log-row__event">
                                    {{
                                        formatLogEvent(
                                            log.event ||
                                                log.action_label ||
                                                log.action
                                        )
                                    }}
                                </span>
                                <span class="log-row__meta">
                                    {{
                                        log.causer_name ||
                                        log.actor?.name ||
                                        log.user?.name ||
                                        '系統'
                                    }}
                                    ·
                                    {{ formatDateTime(log.created_at) }}
                                </span>
                            </div>
                            <template
                                v-if="
                                    logsType === 'inventory' ||
                                    logsType === 'submission'
                                "
                            >
                                <p
                                    v-for="diff in logsType === 'inventory'
                                        ? buildInventoryLogDiff(
                                              log.old_values,
                                              log.new_values
                                          )
                                        : buildSubmissionLogDiff(
                                              log.old_values,
                                              log.new_values
                                          )"
                                    :key="diff.key"
                                    class="log-row__change"
                                >
                                    {{ diff.label }}
                                    <template v-if="diff.hasOld">
                                        {{ diff.oldVal }} →
                                    </template>
                                    {{ diff.newVal }}
                                </p>
                            </template>
                        </div>
                    </div>
                </div>
            </a-spin>
        </a-modal>
    </div>
</template>

<style scoped>
.order-detail-content-wrapper {
    overflow: visible;
}

.order-detail-content-wrapper :deep(.vaporwave-tabs) {
    overflow: visible;
}

.section-title {
    font-size: 16px;
    font-weight: 700;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    margin-bottom: 12px;
    padding-left: 8px;
    border-left: 4px solid rgba(42, 74, 122, 0.55);
    background: rgba(248, 249, 250, 0.8);
    padding-top: 4px;
    padding-bottom: 4px;
}

.section-subtitle {
    margin-top: 4px;
    padding-left: 12px;
    font-size: 12px;
    color: #8a8a9a;
}

.invoice-mono {
    font-family: 'Courier New', monospace;
    font-size: 12px;
}

:deep(.ant-descriptions-item-label) {
    background: rgba(255, 255, 255, 0.92) !important;
    font-weight: 600 !important;
    width: 140px;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
    border-color: rgba(200, 200, 210, 0.4) !important;
}

:deep(.ant-descriptions-item-content) {
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
    background: rgba(255, 255, 255, 0.88) !important;
    border-color: rgba(200, 200, 210, 0.4) !important;
}

.font-mono {
    font-family: 'Courier New', monospace;
}

.tab-scroll-content {
    padding: 16px 0 28px;
}

.detail-section--status {
    padding-bottom: 8px;
}

.detail-section--status :deep(.ant-table-wrapper) {
    margin-bottom: 0;
}

.detail-section--status :deep(.ant-table) {
    background: #fff;
}

.detail-section--status :deep(.ant-table-tbody > tr > td) {
    vertical-align: middle;
    padding: 10px 12px;
}

.detail-section--status :deep(.ant-table-tbody > tr:last-child > td) {
    border-bottom: 1px solid #f0f0f0;
}

.item-seq {
    color: #64748b;
    font-variant-numeric: tabular-nums;
    font-size: 13px;
}

.status-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 4px 12px;
}

.action-link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: #5b6ee1;
    font-size: 13px;
    line-height: 1.4;
    white-space: nowrap;
    text-decoration: none;
    border-bottom: 1px solid transparent;
    transition:
        color 0.15s ease,
        border-color 0.15s ease;
}

.action-link:hover {
    color: #3d4fcf;
    border-bottom-color: rgba(61, 79, 207, 0.45);
}

.action-empty {
    color: #cbd5e1;
}

.total-amount {
    text-shadow: none;
}

:deep(.ant-tabs-nav::before) {
    border-bottom: 1px solid rgba(42, 74, 122, 0.2) !important;
}

:deep(.ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn) {
    color: #7c5cbf !important;
    text-shadow: none;
}

:deep(.ant-tabs-ink-bar) {
    background: #7c5cbf !important;
}
pre {
    margin-bottom: 0;
}

/* 區塊標題旁次要按鈕（編輯匯款、狀態歷程） */
.btn-secondary {
    border: 1px solid #d0d5dd !important;
    color: #475569 !important;
    background: #fff !important;
    border-radius: 6px !important;
    font-weight: 500 !important;
    box-shadow: none !important;
}
.btn-secondary:hover {
    border-color: #94a3b8 !important;
    color: #1e293b !important;
    background: #f8fafc !important;
}

/* 操作紀錄彈窗 */
.logs-modal-body {
    min-height: 80px;
    max-height: min(60vh, 480px);
    overflow-y: auto;
    padding: 0 2px 4px 0;
}

.logs-empty {
    margin: 28px 0;
}

.logs-list {
    display: flex;
    flex-direction: column;
}

.log-row {
    padding: 10px 0;
    border-bottom: 1px solid #f0f0f0;
}

.log-row:last-child {
    border-bottom: none;
}

.log-row__head {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: space-between;
    gap: 4px 12px;
}

.log-row__event {
    font-weight: 600;
    font-size: 13px;
    color: #334155;
}

.log-row__meta {
    font-size: 12px;
    color: #94a3b8;
    font-variant-numeric: tabular-nums;
}

.log-row__change {
    margin: 4px 0 0;
    font-size: 12px;
    line-height: 1.45;
    color: #64748b;
}
</style>
